import type { WorkflowCompletion } from '@generated/graphql';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import type { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { FISCAL_YEAR_SELECT_TEST_ID } from '@ovComponents/4-module/workflowCompletion/constants/testIds';
import { isInternalTransferFormEnabled } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositAccountRestrictions';
import { showFiscalYearBasedOnAccountType } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositFiscalYear';
import {
  internalTransferSourceFilter,
  withdrawSubAccountFilter,
} from '@ovComponents/4-module/workflowCompletion/subSteps/withdrawForm/withdrawSubAccountFilter';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { Typography } from 'hephaestus/web';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { DepositAmount } from '../DepositAmount';
import { showsRequiredFieldError } from '../depositFieldErrors';
import { DepositInternalTransferCurrencyBalanceDisplay } from './DepositInternalTransferCurrencyBalanceDisplay';
import { DepositInternalTransferCurrencySelect } from './DepositInternalTransferCurrencySelect';
import {
  sharesCurrencyWithSelection,
  shouldClearCounterpartSubAccount,
} from './depositInternalTransferCurrencies';
import type { useDepositInternalTransfer } from './useDepositInternalTransfer';

type TransferFieldOption = {
  enabled?: boolean;
  required?: FieldRequiredType;
  label?: unknown;
};

export type DepositInternalTransferProp = {
  deposit: TransferContextData;
  options: {
    fromSubAccount?: TransferFieldOption;
    subAccount?: TransferFieldOption;
    amountCents?: TransferFieldOption;
    fiscalYear?: TransferFieldOption;
    displayCustodianAccountNumber?: boolean;
    selectFirstSubAccountOnly?: boolean;
  };
  focused: string[];
  onFocus: (field: string) => void;
  onChange: (value: Partial<TransferContextData>) => void;
  amountError: { hasError: boolean; errorMessage: string };
  userId?: string;
  workflowCompletion?: WorkflowCompletion;
  transfer: ReturnType<typeof useDepositInternalTransfer>;
  onFormEnabledChange: (enabled: boolean) => void;
  fiscalYearMenuItems: ReactNode[];
};

export const DepositInternalTransfer = ({
  deposit,
  options,
  focused,
  onFocus,
  onChange,
  amountError,
  userId,
  workflowCompletion,
  transfer,
  onFormEnabledChange,
  fiscalYearMenuItems,
}: DepositInternalTransferProp) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const {
    enableMultiCurrencyDepositTransfers,
    availableCurrencies,
    currencyBalance,
    amountCurrency,
    isNearFullWithdrawal,
  } = transfer;

  const showToSubAccountSelect =
    options?.subAccount?.enabled &&
    deposit.objectType !== PageObjectType.SUB_ACCOUNT;
  const fixedToSubAccountIds =
    !showToSubAccountSelect && deposit?.subAccount?.id
      ? [deposit?.subAccount?.id]
      : [];
  const filterArgs = {
    contextObjectType: deposit?.objectType,
    contextObjectId: deposit?.objectId,
    completionObjectType: workflowCompletion?.objectType,
    completionObjectId: workflowCompletion?.objectId,
    userId,
  };

  const handleInternalTransferFromValidation = (e: any) => {
    const isFormEnabled = isInternalTransferFormEnabled({
      selectedSubAccount: e,
      counterpartSubAccount: deposit?.subAccount,
      applicableData: deposit,
    });
    onFormEnabledChange(isFormEnabled);
  };

  const handleInternalTransferToValidation = (e: any) => {
    const isFormEnabled = isInternalTransferFormEnabled({
      selectedSubAccount: e,
      counterpartSubAccount: deposit?.fromSubAccount,
      applicableData: deposit,
    });
    onFormEnabledChange(isFormEnabled);
  };

  return (
    <>
      {options?.fromSubAccount?.enabled && (
        <SubAccountSelect
          omitAny
          sx={{ mb: 2 }}
          userId={userId}
          filter={internalTransferSourceFilter(filterArgs)}
          dataTestId='deposit-form-from-sub-account-select'
          label={translateBackendString(options?.fromSubAccount?.label)}
          subAccountsToExclude={fixedToSubAccountIds}
          filterSubAccount={(subAccount: SubAccount) =>
            sharesCurrencyWithSelection({
              subAccount,
              selectedSubAccount: deposit?.subAccount,
              enableMultiCurrencyDepositTransfers,
            })
          }
          onSubAccountSelect={(e: any) => {
            handleInternalTransferFromValidation(e);
            const shouldClearTo = shouldClearCounterpartSubAccount({
              selectedSubAccount: e,
              counterpartSubAccount: deposit?.subAccount,
              enableMultiCurrencyDepositTransfers,
            });

            if (shouldClearTo) {
              onChange({
                subAccount: undefined,
                fromSubAccount: e,
                currency: undefined,
              });
            } else {
              onChange({ fromSubAccount: e, currency: undefined });
            }
          }}
          selectedSubAccount={deposit?.fromSubAccount}
          onBlur={() => onFocus('fromSubAccount')}
          error={showsRequiredFieldError({
            missing: !deposit?.fromSubAccount,
            field: 'fromSubAccount',
            focused,
            requirement: options?.fromSubAccount?.required,
          })}
          showCurrencyAvatar={!!enableMultiCurrencyDepositTransfers}
          displayCustodianAccountNumber={options.displayCustodianAccountNumber}
          selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
        />
      )}
      {showToSubAccountSelect && (
        <SubAccountSelect
          omitAny
          filter={withdrawSubAccountFilter(filterArgs)}
          sx={{ mb: 2 }}
          dataTestId='deposit-form-sub-account-select'
          label={translateBackendString(options?.subAccount?.label)}
          subAccountsToExclude={
            deposit?.fromSubAccount?.id ? [deposit?.fromSubAccount?.id] : []
          }
          filterSubAccount={(subAccount: SubAccount) =>
            sharesCurrencyWithSelection({
              subAccount,
              selectedSubAccount: deposit?.fromSubAccount,
              enableMultiCurrencyDepositTransfers,
            })
          }
          onSubAccountSelect={(e: any) => {
            handleInternalTransferToValidation(e);
            const shouldClearFrom = shouldClearCounterpartSubAccount({
              selectedSubAccount: e,
              counterpartSubAccount: deposit?.fromSubAccount,
              enableMultiCurrencyDepositTransfers,
            });

            if (shouldClearFrom) {
              onChange({
                fromSubAccount: undefined,
                subAccount: e,
                currency: undefined,
              });
            } else {
              onChange({ subAccount: e, currency: undefined });
            }
          }}
          selectedSubAccount={deposit?.subAccount}
          onBlur={() => onFocus('subAccount')}
          error={showsRequiredFieldError({
            missing:
              !deposit?.subAccount ||
              Object.keys(deposit.subAccount).length === 0,
            field: 'subAccount',
            focused,
            requirement: options?.subAccount?.required,
          })}
          showCurrencyAvatar={!!enableMultiCurrencyDepositTransfers}
          displayCustodianAccountNumber={options.displayCustodianAccountNumber}
          selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
        />
      )}
      {enableMultiCurrencyDepositTransfers &&
        deposit?.fromSubAccount?.id &&
        deposit?.subAccount?.id &&
        availableCurrencies.length > 1 && (
          <DepositInternalTransferCurrencySelect
            selectedCurrency={deposit?.currency}
            availableCurrencies={availableCurrencies}
            focused={focused}
            onFocus={onFocus}
            onChange={onChange}
          />
        )}
      {options?.amountCents?.enabled && (
        <>
          <DepositAmount
            amountCents={deposit?.amountCents}
            label={translateBackendString(options.amountCents.label)}
            requirement={options.amountCents.required}
            focused={focused}
            onFocus={onFocus}
            onChange={onChange}
            amountError={amountError}
            currency={amountCurrency}
            sx={{ mb: 1 }}
          />
          {amountCurrency && currencyBalance && (
            <DepositInternalTransferCurrencyBalanceDisplay
              amountCents={deposit?.amountCents}
              currencyBalance={currencyBalance}
            />
          )}
        </>
      )}
      {!enableMultiCurrencyDepositTransfers && isNearFullWithdrawal && (
        <Typography
          variant='labelSmall'
          component='div'
          color='error'
          sx={{ mt: 1 }}
        >
          {t('depositReview.amountExceeds')}
        </Typography>
      )}

      {showFiscalYearBasedOnAccountType(deposit?.subAccount?.account?.type) && (
        <SelectField
          fullWidth
          sx={{ mb: 2 }}
          testId={FISCAL_YEAR_SELECT_TEST_ID.SELECT}
          value={deposit?.fiscalYear}
          label={translateBackendString(options?.fiscalYear?.label)}
          onChange={(e: any) => onChange({ fiscalYear: e.target.value })}
        >
          {fiscalYearMenuItems}
        </SelectField>
      )}
    </>
  );
};
